import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter,
} from "../components/ui/drawer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CaretDown, Check } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useWarehouses, LAST_WAREHOUSE_KEY } from "../lib/useWarehouses";

export function NewQuoteSheet({ open, onOpenChange }) {
  const { createCart } = useCart();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { selectable: warehouses } = useWarehouses();
  const [warehouseId, setWarehouseId] = useState(() => localStorage.getItem(LAST_WAREHOUSE_KEY) || null);
  const isWarehouseMgr = user?.role === "sample_warehouse";

  useEffect(() => {
    if (open) {
      api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
      setName("");
      setCustomerId(null);
    }
  }, [open]);

  const selectedCustomer = customers.find((c) => c.id === customerId);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const cart = await createCart(name.trim(), { sample_warehouse_id: isWarehouseMgr ? null : warehouseId });
      if (warehouseId) localStorage.setItem(LAST_WAREHOUSE_KEY, warehouseId);
      if (customerId) {
        await api.put(`/carts/${cart.id}`, { customer_id: customerId });
      }
      onOpenChange(false);
      navigate(`/m/quotes/${cart.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create quote");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mobile-app rounded-t-[28px] border-[#E7E5E4] max-h-[85vh]" data-testid="mobile-new-quote-sheet">
        <DrawerHeader>
          <DrawerTitle className="font-display text-2xl text-[#1C1917]">New Quotation</DrawerTitle>
        </DrawerHeader>
        <form onSubmit={handleCreate} className="px-5 pb-5 space-y-5">
          <div>
            <Label className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">Quote Name</Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Fall Collection - Acme Corp"
              data-testid="mobile-new-quote-name-input"
              className="mt-2 h-13 rounded-[14px] border-[#E7E5E4] text-base"
            />
          </div>
          {!isWarehouseMgr && (
            <div>
              <Label className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">Sample Warehouse</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="mobile-new-quote-warehouse">
                <button type="button" onClick={() => setWarehouseId(null)} data-testid="mobile-new-quote-warehouse-none"
                  className={`press-scale text-sm px-3.5 py-2 rounded-full border ${!warehouseId ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#78716C] border-[#E7E5E4]"}`}>None</button>
                {warehouses.map((w) => (
                  <button key={w.id} type="button" onClick={() => setWarehouseId(w.id)} data-testid={`mobile-new-quote-warehouse-${w.id}`}
                    className={`press-scale text-sm px-3.5 py-2 rounded-full border ${warehouseId === w.id ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#1C1917] border-[#E7E5E4]"}`}>{w.name}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">Customer (optional)</Label>
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              data-testid="mobile-new-quote-customer-trigger"
              className="mt-2 w-full h-13 rounded-[14px] border border-[#E7E5E4] bg-white px-4 flex items-center justify-between text-base"
            >
              <span className={selectedCustomer ? "text-[#1C1917]" : "text-[#78716C]"}>
                {selectedCustomer ? selectedCustomer.name : "Select a customer"}
              </span>
              <CaretDown size={16} className="text-[#78716C]" />
            </button>
            {pickerOpen && (
              <div className="mt-2 max-h-52 overflow-y-auto rounded-[14px] border border-[#E7E5E4] bg-white divide-y divide-[#F1F0EE]">
                <button
                  type="button"
                  onClick={() => { setCustomerId(null); setPickerOpen(false); }}
                  className="w-full text-left px-4 py-3 text-sm text-[#78716C]"
                >
                  No customer
                </button>
                {customers.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setCustomerId(c.id); setPickerOpen(false); }}
                    data-testid={`mobile-new-quote-customer-option-${c.id}`}
                    className="w-full text-left px-4 py-3 text-sm text-[#1C1917] flex items-center justify-between"
                  >
                    {c.name}
                    {customerId === c.id && <Check size={16} className="text-[#1E3A8A]" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          <DrawerFooter className="px-0">
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              data-testid="mobile-new-quote-submit"
              className="press-scale w-full h-14 rounded-[16px] bg-[#1E3A8A] text-white font-semibold text-base disabled:opacity-50 transition-colors duration-200"
            >
              {submitting ? "Creating..." : "Create Quotation"}
            </button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
}
